import { _ as __vitePreload } from "main/preload-helper";
class MembershipTierValidator {
  constructor() {
    this.tierOrder = [];
    this.membershipTierConfig = [{
      name: "Support",
      frequencies: [{
        name: "Annually",
        id: 2021294246
      }, {
        name: "Quarterly",
        id: 2080374950
      }, {
        name: "Monthly",
        id: 2021261478
      }]
    }, {
      name: "All Access",
      frequencies: [{
        name: "Annually",
        id: 1574994086
      }, {
        name: "Quarterly",
        id: 1580236966
      }, {
        name: "Monthly",
        id: 1574961318
      }]
    }, {
      name: "Media",
      frequencies: [{
        name: "Annually",
        id: 1575059622
      }, {
        name: "Quarterly",
        id: 1580204198
      }, {
        name: "Monthly",
        id: 1575026854
      }]
    }];
    this.loadTierOrder();
  }
  /**
   * Public getter for tier order (for debugging)
   */
  get debugTierOrder() {
    return [...this.tierOrder];
  }
  /**
   * Load tier priority order from theme settings
   */
  loadTierOrder() {
    var _a, _b;
    try {
      const tierOrderSetting = ((_b = (_a = window.theme) == null ? void 0 : _a.settings) == null ? void 0 : _b.membership_tier_priority_order) || "2021294246, 2080374950, 2021261478, 1574994086, 1580236966, 1574961318, 1575059622, 1580204198, 1575026854";
      this.tierOrder = tierOrderSetting.split(",").map((id) => parseInt(id.trim())).filter((id) => !isNaN(id));
      console.log("Loaded membership tier order:", this.tierOrder);
    } catch (error) {
      console.error("Error loading tier order from settings:", error);
      this.tierOrder = [2021294246, 2080374950, 2021261478, 1574994086, 1580236966, 1574961318, 1575059622, 1580204198, 1575026854];
    }
  }
  /**
   * Get the priority index of a selling plan ID (lower index = higher priority)
   */
  getTierPriority(sellingPlanId) {
    const index = this.tierOrder.indexOf(sellingPlanId);
    return index === -1 ? 999 : index;
  }
  /**
   * Check if a product is a membership product based on its selling plans
   */
  isMembershipProduct(sellingPlanId) {
    return this.tierOrder.includes(sellingPlanId);
  }
  /**
   * Get current cart contents
   */
  async getCurrentCart() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        throw new Error("Failed to fetch cart");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching cart:", error);
      return {
        items: []
      };
    }
  }
  /**
   * Get user's current membership level from their account
   */
  async getCurrentUserMembershipLevel() {
    try {
      const {
        membershipService
      } = await __vitePreload(async () => {
        const {
          membershipService: membershipService2
        } = await import("main/membership-service").then((n) => n.b);
        return {
          membershipService: membershipService2
        };
      }, true ? [] : void 0, import.meta.url);
      const membershipStatus = await membershipService.getCurrentMembershipStatus();
      if (membershipStatus.status === "not-member" || membershipStatus.memberships.length === 0) {
        return null;
      }
      let highestPriorityTier = null;
      let highestPriorityIndex = 999;
      for (const membership of membershipStatus.memberships) {
        const tierConfig = this.membershipTierConfig.find((tier) => tier.name === membership.name);
        if (!tierConfig) {
          console.warn(`[MembershipValidator] Unknown membership tier: ${membership.name}`);
          continue;
        }
        const frequencyConfig = tierConfig.frequencies.find((freq) => freq.name === membership.selectedPlanOption);
        if (!frequencyConfig) {
          console.warn(`[MembershipValidator] Unknown frequency for ${membership.name}: ${membership.selectedPlanOption}`);
          continue;
        }
        const planId = frequencyConfig.id;
        const priorityIndex = this.getTierPriority(planId);
        if (priorityIndex < highestPriorityIndex) {
          highestPriorityIndex = priorityIndex;
          highestPriorityTier = planId;
        }
      }
      console.log(`[MembershipValidator] User's highest priority membership tier: ${highestPriorityTier} (priority: ${highestPriorityIndex})`);
      return highestPriorityTier;
    } catch (error) {
      console.error("Error getting user membership level:", error);
      return null;
    }
  }
  /**
   * Validate if a membership product can be added to cart
   */
  async validateMembershipAddToCart(sellingPlanId) {
    console.log("[MembershipValidator] Validating selling plan ID:", sellingPlanId);
    console.log("[MembershipValidator] Current tier order:", this.tierOrder);
    if (!this.isMembershipProduct(sellingPlanId)) {
      console.log("[MembershipValidator] Not a membership product, allowing");
      return {
        canAddToCart: true,
        reason: "Not a membership product",
        messageType: "cart-change"
      };
    }
    console.log("[MembershipValidator] Is membership product, proceeding with validation");
    const [cart, userMembershipTier] = await Promise.all([this.getCurrentCart(), this.getCurrentUserMembershipLevel()]);
    console.log("[MembershipValidator] Cart items:", cart.items.length);
    console.log("[MembershipValidator] User membership tier:", userMembershipTier);
    const requestedTierPriority = this.getTierPriority(sellingPlanId);
    console.log("[MembershipValidator] Requested tier priority:", requestedTierPriority);
    const existingMembershipItems = cart.items.filter((item) => item.selling_plan_allocation && this.isMembershipProduct(item.selling_plan_allocation.selling_plan.id));
    console.log("[MembershipValidator] Existing membership items in cart:", existingMembershipItems.length);
    if (existingMembershipItems.length > 0) {
      const existingTier = existingMembershipItems[0].selling_plan_allocation.selling_plan.id;
      const existingTierPriority = this.getTierPriority(existingTier);
      console.log("[MembershipValidator] Found existing membership:", existingTier, "with priority:", existingTierPriority);
      return {
        canAddToCart: false,
        reason: "Change the membership level in your cart",
        messageType: "cart-change",
        existingMembershipTier: existingTier,
        requestedMembershipTier: sellingPlanId
      };
    }
    if (userMembershipTier !== null) {
      const userTierPriority = this.getTierPriority(userMembershipTier);
      console.log("[MembershipValidator] User has membership tier:", userMembershipTier, "with priority:", userTierPriority);
      if (requestedTierPriority < userTierPriority) {
        console.log("[MembershipValidator] Blocking downgrade attempt");
        return {
          canAddToCart: false,
          reason: "Change your membership level via your account portal",
          messageType: "account-upgrade",
          currentUserTier: userMembershipTier,
          requestedMembershipTier: sellingPlanId
        };
      }
      console.log("[MembershipValidator] User tier validation passed");
    }
    console.log("[MembershipValidator] All validation passed, allowing add to cart");
    return {
      canAddToCart: true,
      reason: "Validation passed",
      messageType: "cart-change"
    };
  }
  /**
   * Validate cart to ensure no more than 1 membership and quantity of 1 for each membership
   * Automatically fixes violations by removing/adjusting items
   */
  async validateCart() {
    console.log("[MembershipValidator] Starting cart validation");
    const cart = await this.getCurrentCart();
    const errors = [];
    let itemsRemoved = 0;
    let cartModified = false;
    const membershipItems = cart.items.filter((item) => item.selling_plan_allocation && this.isMembershipProduct(item.selling_plan_allocation.selling_plan.id));
    console.log("[MembershipValidator] Found", membershipItems.length, "membership items in cart");
    const userCurrentMembershipLevel = await this.getCurrentUserMembershipLevel();
    if (membershipItems.length > 1) {
      console.log("[MembershipValidator] Multiple memberships found, removing lower priority items");
      const sortedItems = membershipItems.sort((a, b) => {
        const priorityA = this.getTierPriority(a.selling_plan_allocation.selling_plan.id);
        const priorityB = this.getTierPriority(b.selling_plan_allocation.selling_plan.id);
        return priorityA - priorityB;
      });
      const itemsToRemove = sortedItems.slice(1);
      for (const item of itemsToRemove) {
        try {
          console.log("[MembershipValidator] Removing lower priority membership:", item.selling_plan_allocation.selling_plan.name);
          const changeRequest = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              id: item.key.toString(),
              quantity: 0
            })
          });
          if (changeRequest.ok) {
            const changeResponse = await changeRequest.json();
            console.log("[MembershipValidator] Successfully removed item:", changeResponse);
            itemsRemoved++;
            cartModified = true;
          } else {
            console.error("[MembershipValidator] Failed to remove item:", await changeRequest.text());
            errors.push("Unable to remove duplicate membership");
          }
        } catch (error) {
          console.error("[MembershipValidator] Error removing item:", error);
          errors.push("Unable to remove duplicate membership");
        }
      }
    }
    if (userCurrentMembershipLevel !== null) {
      const userTierPriority = this.getTierPriority(userCurrentMembershipLevel);
      console.log(`[MembershipValidator] User has active membership with priority: ${userTierPriority}`);
      const updatedCart = await this.getCurrentCart();
      const updatedMembershipItems = updatedCart.items.filter((item) => item.selling_plan_allocation && this.isMembershipProduct(item.selling_plan_allocation.selling_plan.id));
      const lowerTierItems = updatedMembershipItems.filter((item) => {
        const cartTierPriority = this.getTierPriority(item.selling_plan_allocation.selling_plan.id);
        return cartTierPriority > userTierPriority;
      });
      if (lowerTierItems.length > 0) {
        console.log(`[MembershipValidator] Found ${lowerTierItems.length} lower-tier memberships to remove`);
        for (const item of lowerTierItems) {
          try {
            console.log("[MembershipValidator] Removing lower-tier membership:", item.selling_plan_allocation.selling_plan.name, "because user has higher tier membership");
            const changeRequest = await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                id: item.key.toString(),
                quantity: 0
              })
            });
            if (changeRequest.ok) {
              const changeResponse = await changeRequest.json();
              console.log("[MembershipValidator] Successfully removed lower-tier item:", changeResponse);
              itemsRemoved++;
              cartModified = true;
            } else {
              console.error("[MembershipValidator] Failed to remove lower-tier item:", await changeRequest.text());
              errors.push("Unable to remove lower-tier membership");
            }
          } catch (error) {
            console.error("[MembershipValidator] Error removing lower-tier item:", error);
            errors.push("Unable to remove lower-tier membership");
          }
        }
      }
    }
    for (const item of membershipItems) {
      if (item.quantity > 1) {
        try {
          console.log("[MembershipValidator] Reducing membership quantity to 1 for item:", item.selling_plan_allocation.selling_plan.id);
          const changeRequest = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              id: item.key.toString(),
              quantity: 1
            })
          });
          if (changeRequest.ok) {
            console.log("[MembershipValidator] Successfully reduced quantity to 1");
            cartModified = true;
          } else {
            console.error("[MembershipValidator] Failed to reduce quantity:", await changeRequest.text());
            errors.push("Unable to adjust membership quantity");
          }
        } catch (error) {
          console.error("[MembershipValidator] Error reducing item quantity:", error);
          errors.push("Unable to adjust membership quantity");
        }
      }
    }
    if (cartModified) {
      console.log("[MembershipValidator] Cart modified, triggering update events");
      setTimeout(async () => {
        var _a, _b;
        if ((_b = (_a = window.Shopify) == null ? void 0 : _a.cart) == null ? void 0 : _b.refreshCart) {
          await window.Shopify.cart.refreshCart();
        } else {
          window.dispatchEvent(new CustomEvent("theme:cart:change"));
          window.dispatchEvent(new CustomEvent("theme:cart:refresh"));
        }
        window.dispatchEvent(new CustomEvent("membership:cart:validated", {
          detail: {
            itemsRemoved,
            errors
          }
        }));
      }, 100);
    }
    const result = {
      isValid: errors.length === 0,
      errors,
      itemsRemoved
    };
    console.log("[MembershipValidator] Cart validation result:", result);
    return result;
  }
  /**
   * Remove lower priority memberships from cart when adding a higher priority one
   * Lower priority = higher index in tier order array
   */
  async removeLowerPriorityMemberships(newSellingPlanId) {
    console.log("[MembershipValidator] Removing lower priority memberships for new tier:", newSellingPlanId);
    const cart = await this.getCurrentCart();
    const newTierPriority = this.getTierPriority(newSellingPlanId);
    let itemsRemoved = 0;
    const itemsToRemove = cart.items.filter((item) => {
      if (!item.selling_plan_allocation || !this.isMembershipProduct(item.selling_plan_allocation.selling_plan.id)) {
        return false;
      }
      const itemTierPriority = this.getTierPriority(item.selling_plan_allocation.selling_plan.id);
      return itemTierPriority > newTierPriority;
    });
    console.log("[MembershipValidator] Found", itemsToRemove.length, "lower priority items to remove");
    for (const item of itemsToRemove) {
      try {
        console.log("[MembershipValidator] Removing lower priority item:", item.selling_plan_allocation.selling_plan.name);
        const changeRequest = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: item.key.toString(),
            quantity: 0
          })
        });
        if (changeRequest.ok) {
          const changeResponse = await changeRequest.json();
          console.log("[MembershipValidator] Successfully removed item:", changeResponse);
          itemsRemoved++;
        } else {
          console.error("[MembershipValidator] Failed to remove item:", await changeRequest.text());
        }
      } catch (error) {
        console.error("[MembershipValidator] Error removing item from cart:", error);
      }
    }
    if (itemsRemoved > 0) {
      console.log("[MembershipValidator] Removed", itemsRemoved, "items, triggering update events");
      setTimeout(async () => {
        var _a, _b;
        if ((_b = (_a = window.Shopify) == null ? void 0 : _a.cart) == null ? void 0 : _b.refreshCart) {
          await window.Shopify.cart.refreshCart();
        } else {
          window.dispatchEvent(new CustomEvent("theme:cart:change"));
          window.dispatchEvent(new CustomEvent("theme:cart:refresh"));
        }
        window.dispatchEvent(new CustomEvent("membership:lower-priority:removed", {
          detail: {
            itemsRemoved,
            newSellingPlanId
          }
        }));
      }, 100);
    }
  }
}
const membershipTierValidator = new MembershipTierValidator();
if (typeof window !== "undefined") {
  window.membershipTierValidator = membershipTierValidator;
  window.debugMembershipTiers = {
    testValidation: async (sellingPlanId) => {
      console.log("=== TESTING MEMBERSHIP VALIDATION ===");
      const result = await membershipTierValidator.validateMembershipAddToCart(sellingPlanId);
      console.log("Validation result:", result);
      return result;
    },
    validateCart: async () => {
      console.log("=== TESTING CART VALIDATION ===");
      const result = await membershipTierValidator.validateCart();
      console.log("Cart validation result:", result);
      return result;
    },
    showTierOrder: () => {
      console.log("=== CURRENT TIER ORDER ===");
      const tierOrder = membershipTierValidator.debugTierOrder;
      console.log("Tier order:", tierOrder);
      tierOrder.forEach((tier, index) => {
        console.log(`${index}: ${tier} (${index === 0 ? "Lowest" : index === tierOrder.length - 1 ? "Highest" : "Mid"} Priority)`);
      });
    },
    checkCart: async () => {
      console.log("=== CURRENT CART STATUS ===");
      const cart = await membershipTierValidator.getCurrentCart();
      console.log("Total items:", cart.items.length);
      const membershipItems = cart.items.filter((item) => item.selling_plan_allocation && membershipTierValidator.isMembershipProduct(item.selling_plan_allocation.selling_plan.id));
      console.log("Membership items:", membershipItems.length);
      membershipItems.forEach((item) => {
        const priority = membershipTierValidator.getTierPriority(item.selling_plan_allocation.selling_plan.id);
        console.log(`  - ${item.selling_plan_allocation.selling_plan.name} (ID: ${item.selling_plan_allocation.selling_plan.id}, Priority: ${priority}, Qty: ${item.quantity})`);
      });
      return {
        cart,
        membershipItems
      };
    }
  };
}
export {
  membershipTierValidator
};
